import { AppointmentModel, ReferrerModel, Resource } from '@mmx/shared'

/**
 * Base class that provides physician display logic shared between
 * AppointmentCardComponent and PatientAppointmentComponent.
 *
 * Subclasses must expose an `appointment` property.
 */
export abstract class AppointmentPhysicianBase {
  abstract appointment: AppointmentModel

  get attendingPhysician(): Resource | undefined {
    if (!this.appointment?.attendingPhysicianId || !this.appointment.resources) {
      return undefined
    }

    return this.appointment.resources.find((resource) => resource.id === this.appointment?.attendingPhysicianId)
  }

  get referringPhysician(): ReferrerModel | undefined {
    if (!this.appointment?.referringPhysicianId || !this.appointment.referrer) {
      return undefined
    }

    return this.appointment.referrer.id === this.appointment.referringPhysicianId
      ? this.appointment.referrer
      : undefined
  }

  get orderingPhysicianDisplay(): string | undefined {

    if (this.appointment?.orderingPhysicianId && this.appointment?.resources) {
      const orderingResource = this.appointment.resources.find((resource) => resource.id === this.appointment?.orderingPhysicianId)
      if (orderingResource) {
        return this.appendNpi(orderingResource.name || '', orderingResource.npi)
      }
    }

    if (!this.appointment?.orderingPhysician) {
      return undefined
    }

    const orderingPhysician = this.appointment.orderingPhysician.trim().toUpperCase()
    const referrerShortName = this.appointment.referrer?.shortName
    const orderingNpi = this.namesMatch(orderingPhysician, referrerShortName)
      ? this.appointment.referrer?.npi
      : undefined

    return this.appendNpi(orderingPhysician, orderingNpi)
  }

  formatPhysicianResource(resource: Resource): string {
    return this.appendNpi(resource.name || '', resource.npi)
  }

  formatPhysicianReferrer(referrer: ReferrerModel): string {
    return this.appendNpi(referrer.shortName, referrer.npi)
  }

  private appendNpi(name: string, npi?: string): string {
    if (!name) {
      return ''
    }

    return npi ? `${name} (NPI: ${npi})` : name
  }

  private namesMatch(firstName?: string, secondName?: string): boolean {
    if (!firstName || !secondName) {
      return false
    }

    return firstName.trim().toUpperCase() === secondName.trim().toUpperCase()
  }
}
